import { isRussia, isUS, isUserProductTagsEnabled } from 'env'
import { useDisplayClearBtn } from 'hooks/useDisplayClearBtn'
import { ProfileType } from 'modules/domain/user/types'
import { endpoints } from 'modules/endpoints'
import { TableFiltersProps } from 'modules/types'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AddressFilter } from 'views/components/AddressFilter/AddressFilter'
import { ACheckboxV2, AClearFiltersButton, ASearch, ASelectFilter } from 'views/components/Analytics'
import {
  FiltersAndSearchWrapper,
  FiltersWrapper,
  SearchWrapper,
} from 'views/components/CommonTableComponents/CommonTableComponents'
import { InputFilter } from 'views/components/InputFilter/InputFilter'
import { CropsFilter } from 'views/components/TableFilters'
import { ABaseTableFilter } from 'views/components/TableFilters/ABaseTableFilter'
import { ADateRangePicker } from 'views/components/TableFilters/DateRangePicker/ADateRangePicker'
import { CommonRegionFilter } from 'views/components/TableFilters/RegionsFilters/CommonRegionFilter'
import { NewTeamFilter } from 'views/components/TeamFilter/NewTeamFilter'
import { CoordinatorTypeFilter } from 'views/filters/CoordinatorTypeFilter'

export const UserFilters: FC<TableFiltersProps> = ({ setListRequestParams, updateFilterState, listRequestParams }) => {
  const { t } = useTranslation('user')

  const displayClearBtn = useDisplayClearBtn({
    listRequestParams,
  })

  const optionsType = useMemo(() => {
    const types = [
      ProfileType.seller,
      ProfileType.customer,
      ProfileType.carrier,
      ProfileType.broker,
      ProfileType.manager,
    ]
    const filteredTypes = isRussia ? types.filter((type) => type !== ProfileType.manager) : types
    return filteredTypes.map((pType) => ({
      value: pType,
      label: t(`profile_type.${pType}`),
    }))
  }, [t])

  const optionsRole = useMemo(
    () => ['coordinator', 'market_user'].map((role) => ({ value: role, label: t(`role.${role}`) })),
    [t],
  )

  const optionsStatus = [
    { value: 'true', label: t('list.isActive') },
    { value: 'false', label: t('list.inActive') },
  ]

  return (
    <FiltersAndSearchWrapper>
      <FiltersWrapper>
        {!isUS && (
          <ADateRangePicker
            value={[listRequestParams.filter.date_joined_after, listRequestParams.filter.date_joined_before]}
            onChange={(value) =>
              updateFilterState({ date_joined_after: value.startDate, date_joined_before: value.endDate })
            }
            id="dateFilter"
          />
        )}

        <ASelectFilter
          onChange={(profile_type) => updateFilterState({ profile_type })}
          value={listRequestParams.filter.profile_type}
          placeholder={t('type')}
          options={optionsType}
          showSearch={false}
          id="typeFilter"
        />

        {isUS && (
          <InputFilter
            onChange={(days_since_last_comment) => updateFilterState({ days_since_last_comment })}
            value={listRequestParams.filter.days_since_last_comment}
            id="daysFilter"
            suffix={t('short_day')}
            placeholder={t('days')}
            prefix={t('more')}
            type="number"
          />
        )}

        {isUserProductTagsEnabled && !isUS && (
          <CropsFilter
            listRequestParams={listRequestParams}
            updateFilterState={updateFilterState}
            fieldName="product_tag"
          />
        )}
        <ASelectFilter
          onChange={(profile_role) => updateFilterState({ profile_role })}
          value={listRequestParams.filter.profile_role}
          placeholder={t('roleSingle')}
          options={optionsRole}
          showSearch={false}
          id="roleFilter"
        />
        <CoordinatorTypeFilter updateFilterState={updateFilterState} listRequestParams={listRequestParams} />
        <ABaseTableFilter
          onChange={(responsible) => updateFilterState({ responsible })}
          value={listRequestParams.filter.responsible}
          id="responsible"
          endpoint={endpoints.dealCoordinators()}
          getLabel={(dto) => dto.full_name}
          placeholder={t('responsible')}
          showSearch={false}
          multiple
        />
        <NewTeamFilter
          eventId="teamsFilter"
          value={listRequestParams.filter.team}
          updateFilterState={updateFilterState}
        />
        <ASelectFilter
          onChange={(is_active) => updateFilterState({ is_active })}
          placeholder={t('list.tableHeaders.status')}
          value={listRequestParams.filter.is_active}
          options={optionsStatus}
          showSearch={false}
          id="statusFilter"
        />
        <ABaseTableFilter
          onChange={(user_feature) => updateFilterState({ user_feature })}
          value={listRequestParams.filter.user_feature}
          id="featureFilter"
          endpoint={endpoints.userFeature()}
          getLabel={(dto) => dto.label}
          placeholder={t('feature')}
          showSearch={false}
          simpleApiFormat
        />

        <CommonRegionFilter listRequestParams={listRequestParams} updateFilterState={updateFilterState} />

        <AddressFilter filter={listRequestParams.filter} onChange={(searchParams) => updateFilterState(searchParams)} />
        <ACheckboxV2
          onChange={(e) => updateFilterState({ without_tasks: e.target.checked })}
          /* I check for String type because after reloading the page with url, a string is loaded into the "without_tasks" parameter that does not work with the checkbox */
          checked={
            typeof listRequestParams.filter.without_tasks === 'string'
              ? JSON.parse(listRequestParams.filter.without_tasks)
              : listRequestParams.filter.without_tasks
          }
          label={t('noTasks')}
          id="noTasks"
        />
        {displayClearBtn && (
          <AClearFiltersButton
            onClick={() => setListRequestParams && setListRequestParams({ ...listRequestParams, filter: {} })}
          />
        )}
      </FiltersWrapper>

      <SearchWrapper>
        <ASearch
          onChange={(search) => updateFilterState({ search })}
          value={listRequestParams.filter.search}
          placeholder={t('common:search')}
        />
      </SearchWrapper>
    </FiltersAndSearchWrapper>
  )
}
